import React, {useEffect, useMemo} from 'react';
import {useForm, useSearch} from 'b302-frontend-library';
import HanTable, {
    HanTableCell,
    HanTableContainer,
    HanTableRow
} from '../../../../utils/han-table-components/HanTable';
import FormQuestion from '../../../../../networking/models/FormQuestion';
import HanPagination from '../../../../utils/HanPagination';
import {isDesktop} from '../../../../utils/MediaQueries';
import {highlightSearchMatches} from '../../../../utils/SearchUtils';
import HanSearch from '../../../../utils/HanSearch';
import {formQuestionsToTableQuestions, Question} from '../../../../../utils/University';
import QuestionTable from '../QuestionTable';
import SortableTableHead, { SortableTableHeadCell } from '../../../partials/SortableTableHead';
import HanTab from '../../../../utils/han-tabs-components/HanTab';
import HanTabs from '../../../../utils/han-tabs-components/HanTabs';
import {UniversitySubjectsResponse} from '../../../../../networking/responses/University';

interface Props {
    formQuestions: FormQuestion[];
    subjects: UniversitySubjectsResponse;
    onSubjectSearch: (query: string) => void;
    onSubjectsOrderBy: (column: string, direction?: string) => void;

    onPageChange: (page: number) => void;
}

const UniversityProgram: React.FC<Props> = ({ onPageChange, formQuestions, subjects, onSubjectsOrderBy, onSubjectSearch }) => {

    const [form, onFormChange] = useForm<{ searchQuery: string }>(f => ({
        searchQuery: f.input('')
    }));

    const [, searchSubjects] = useSearch('');

    const pageRange = isDesktop ? 17 : 5;
    const questions: Question[] = useMemo(() => formQuestionsToTableQuestions(formQuestions), [formQuestions]);

    useEffect(() => {
        searchSubjects(form.values.searchQuery, onSubjectSearch);
    }, [form.values.searchQuery]);

    const subjectTableHeadCells: SortableTableHeadCell[] = [
        {
            name: 'Subject',
            orderByName: 'name',
        },
        {
            name: 'Level',
            orderByName: 'course_level',
        },
        {
            name: 'Language',
            orderByName: 'language',
        },
        {
            name: 'ECTS',
            orderByName: 'ects',
        },
        {
            name: 'Code',
            orderByName: 'code',
        },
        {
            name: 'Program student',
            orderByName: 'program_student'
        }
    ];

    return (
        <div className={'university-program'}>
                <HanTabs title=''>
                    <HanTab title='Academic Program'>
                        <h2 className={'university-program__title university-program__title--margin'}>Academic program</h2>

                        <div className={'university-program__tables'}>
                            { questions.map(question => (
                                <div className={'university-program__table-section'} key={question.id}>
                                    <QuestionTable question={question} />
                                </div>
                            )) }
                        </div>
                    </HanTab>

                    <HanTab title='Subjects'>
                        <div className={'university-program__title-container'}>
                            <h2 className={'university-program__title university-program__title--margin'}>Subjects</h2>

                            <div className={'university-program__search'}>
                                <HanSearch
                                    id={'searchQuery'}
                                    form={form}
                                    onChange={onFormChange}
                                    placeholder={'Search subjects'} />
                            </div>
                        </div>

                        <div className={'university-program__service-table'}>
                            <HanTableContainer>
                                <HanTable borderOrientation={'horizontal'}>
                                    <SortableTableHead cells={subjectTableHeadCells} onOrderBy={onSubjectsOrderBy} />

                                    { subjects.data.map((subject) => (
                                        <HanTableRow key={subject.id}>
                                            <HanTableCell modifiers={['alabaster', 'left']}>
                                                <span dangerouslySetInnerHTML={{__html: highlightSearchMatches(subject.subject, form.values.searchQuery)}} />
                                            </HanTableCell>
                                            <HanTableCell>{ subject.course_level }</HanTableCell>
                                            <HanTableCell modifiers={['alabaster']}>{ subject.language }</HanTableCell>
                                            <HanTableCell>{ subject.ects }</HanTableCell>
                                            <HanTableCell modifiers={['alabaster']}>{ subject.code }</HanTableCell>
                                            <HanTableCell modifiers={['left']}>{ subject.program_student }</HanTableCell>
                                        </HanTableRow>
                                    )) }
                                </HanTable>
                            </HanTableContainer>
                        </div>

                        <HanPagination
                            pageCount={subjects.lastPage}
                            currentPage={subjects.currentPage}
                            onPageChange={onPageChange}
                            pageRangeDisplayed={pageRange} />
                    </HanTab>
                </HanTabs>
        </div>
    )
}

export default UniversityProgram;
